import { addressRegionOriginSchema } from './address-resolution.js';
import { partnerMapFiltersShape } from './partner-map.js';
import { z } from 'zod';
import { sirenInputSchema, siretInputSchema, refineFrenchCompanyIdentifiers, } from './french-company-identifiers.js';
import { supplierPriceTierSchema } from './product-price-condition.js';
import { geographicSubdivisionFields, validateSubdivision } from './geographic-subdivision.js';
import { CUSTOMER_TAGS_MAX_COUNT, CUSTOMER_TAG_MAX_LENGTH, CUSTOMER_EXPORT_FORMATS, } from '../constants/customers.js';
import { LEAD_SCORE_MAX, LEAD_SCORE_MIN, PARTNER_BLOCK_REASON_MAX_LENGTH, PARTNER_LOCK_RISK_STATUSES, PARTNER_SORT_FIELDS, PARTNER_TYPES, } from '../constants/partners.js';
import { DOCUMENT_LANGUAGES } from '../constants/documents.js';
import { commaSeparated, countryCodeSchema, currencyCodeSchema, idSchema, isoDateSchema, isoDateTimeSchema, moneySchema, percentSchema, } from './common.js';
import { legalFormSchema, internationalLegalFormFields } from './company.js';
import { customFieldFiltersQuerySchema, customFieldValuesSchema } from './custom-field.js';
import { customerIndustrySchema, customerIndustryCodeSchema, customerIndustryCodeInputSchema, vatIdInputSchema, } from './customer.js';
import { customerStatusSchema, customerTypeSchema, creditInsuranceStatusSchema, incotermSchema, leadSourceSchema, leadStatusSchema, partnerRiskStatusSchema, supplierRatingSchema, taxCategorySchema, vatIdStatusSchema, } from './enums.js';
import { paginationMetaSchema, paginationQuerySchema, sortOrderSchema } from './pagination.js';
const optionalText = (max) => z.string().trim().max(max).nullish();
export const partnerTypeSchema = z.enum(PARTNER_TYPES);
export const partnerSortFieldSchema = z.enum(PARTNER_SORT_FIELDS);
export const partnerTagSchema = z.string().trim().min(1).max(CUSTOMER_TAG_MAX_LENGTH);
export const partnerTagsSchema = z.array(partnerTagSchema).max(CUSTOMER_TAGS_MAX_COUNT);
export const partnerCreationModeSchema = z.enum(['QUICK', 'COMPLETE']);
export const paymentTermWarningSchema = z.enum(['CUSTOM_LEGACY', 'INCONSISTENT_LEGACY']);
export const paymentTermOptionSchema = z.object({
    id: idSchema,
    code: z.string(),
    label: z.string(),
    translations: z.record(z.string(), z.string()),
    dueDays: z.number().int().min(0).max(3650),
    cashDiscountPct: percentSchema,
    cashDiscountDays: z.number().int().min(0).max(3650),
    isDefault: z.boolean(),
});
export const paymentTermListResponseSchema = z.object({
    data: z.array(paymentTermOptionSchema),
});
export const debtorAccountSchema = z
    .string()
    .trim()
    .regex(/^\d{4,9}$/, 'Das DATEV-Debitorenkonto besteht aus 4 bis 9 Ziffern.');
export const creditorAccountSchema = z
    .string()
    .trim()
    .regex(/^\d{4,9}$/, 'Das DATEV-Kreditorenkonto besteht aus 4 bis 9 Ziffern.');
const ibanSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/, 'Die IBAN entspricht keinem gültigen Format.');
const bicSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'Die BIC entspricht keinem gültigen Format.');
// ---------------------------------------------------------------------------
// Rollen
// ---------------------------------------------------------------------------
export const partnerRolesSchema = z.object({
    isCustomer: z.boolean(),
    isProspect: z.boolean(),
    isSupplier: z.boolean(),
});
export function hasAnyPartnerRole(roles) {
    return roles.isCustomer === true || roles.isProspect === true || roles.isSupplier === true;
}
// ---------------------------------------------------------------------------
// Partner (Lesemodell)
// ---------------------------------------------------------------------------
export const partnerSchema = z.object({
    hasMigrationSource: z.boolean().optional(),
    migrationReviewRequired: z.boolean().optional(),
    id: idSchema,
    /** Rollenabhängig vergeben: KD- (Kunde), INT- (Interessent), LF- (Lieferant). */
    partnerNumber: z.string(),
    type: customerTypeSchema,
    status: customerStatusSchema,
    /** Abgeleitet aus `status`; die Pflege erfolgt ausschließlich über den Status. */
    isActive: z.boolean(),
    isCustomer: z.boolean(),
    isProspect: z.boolean(),
    isSupplier: z.boolean(),
    companyName: z.string().nullable(),
    tradingName: z.string().nullable(),
    legalForm: legalFormSchema.nullable(),
    ...internationalLegalFormFields,
    department: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    displayName: z.string(),
    searchTerm: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    mobile: z.string().nullable(),
    website: z.string().nullable(),
    street: z.string().nullable(),
    geocodingPublic: z.boolean().default(false),
    houseNumber: z.string().nullable(),
    addressLine2: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    regionOrigin: addressRegionOriginSchema,
    ...geographicSubdivisionFields,
    countryCode: z.string().nullable(),
    vatId: z.string().nullable(),
    vatIdStatus: vatIdStatusSchema,
    vatIdCheckedAt: isoDateTimeSchema.nullable(),
    taxNumber: z.string().nullable(),
    siren: z.string().nullish(),
    siret: z.string().nullish(),
    gln: z.string().nullable(),
    eoriNumber: z.string().nullable(),
    /** Umsatzsteuer-Strategie des Partners (REGULAR ⇒ DOMESTIC, INTRA_COMMUNITY ⇒ EU_B2B …). */
    taxCategory: taxCategorySchema.nullable(),
    preferredLanguage: z.enum(DOCUMENT_LANGUAGES).nullable(),
    currency: z.string(),
    industry: customerIndustrySchema.nullable(),
    industryCode: customerIndustryCodeSchema.nullable(),
    salesTerritory: z.string().nullable(),
    revenueClass: z.string().nullable(),
    ownerUserId: idSchema.nullable(),
    ownerName: z.string().nullable(),
    tags: z.array(z.string()),
    customFields: customFieldValuesSchema,
    // Kunde
    debtorAccountNumber: z.string().nullable(),
    creditLimit: z.string().nullable(),
    creditRating: z.number().int().nullable(),
    priceGroupId: idSchema.nullish(),
    priceGroupName: z.string().nullish(),
    priceGroup: z.string().nullable(),
    defaultDiscountPct: z.string().nullable(),
    paymentTermId: idSchema.nullable(),
    paymentTermCode: z.string().nullable(),
    paymentTermLabel: z.string().nullable(),
    paymentTermWarning: paymentTermWarningSchema.nullable(),
    paymentTermsDays: z.number().int().nullable(),
    discountDays: z.number().int().nullable(),
    discountPercent: z.string().nullable(),
    deliveryTerms: z.string().nullable(),
    deliveryBlock: z.boolean(),
    dunningBlock: z.boolean(),
    minRemainingShelfLifeDays: z.number().int().nullable(),
    // Risikomanagement (Insolvenz, Inkasso, Verkaufssperre)
    riskStatus: partnerRiskStatusSchema,
    isBlocked: z.boolean(),
    blockReason: z.string().nullable(),
    blockedAt: isoDateTimeSchema.nullable(),
    blockedByUserId: idSchema.nullable(),
    blockedByName: z.string().nullable(),
    unblockedAt: isoDateTimeSchema.nullable(),
    unblockedByUserId: idSchema.nullable(),
    unblockedByName: z.string().nullable(),
    unblockReason: z.string().nullable(),
    // Warenkreditversicherung (WKV)
    creditInsuranceStatus: creditInsuranceStatusSchema,
    creditInsuranceCompany: z.string().nullable(),
    creditInsurancePolicyNumber: z.string().nullable(),
    creditInsuranceLimit: z.string().nullable(),
    creditInsuranceDeductiblePercent: z.string().nullable(),
    creditInsuranceClaimDate: isoDateTimeSchema.nullable(),
    creditInsuranceClaimAmount: z.string().nullable(),
    creditInsurancePayoutAmount: z.string().nullable(),
    // Interessent
    leadStatus: leadStatusSchema.nullable(),
    leadSource: leadSourceSchema.nullable(),
    leadScore: z.number().int().nullable(),
    dealSizeEstimated: z.string().nullable(),
    winProbability: z.number().int().nullable(),
    expectedClosingDate: isoDateSchema.nullable(),
    lostReason: z.string().nullable(),
    convertedAt: isoDateTimeSchema.nullable(),
    convertedToCustomerId: idSchema.nullable(),
    // Lieferant
    creditorAccountNumber: z.string().nullable(),
    supplierRating: supplierRatingSchema.nullable(),
    minimumOrderValue: z.string().nullable(),
    deliveryTimeDays: z.number().int().nullable(),
    incoterms: incotermSchema.nullable(),
    supplierIban: z.string().nullable(),
    supplierBic: z.string().nullable(),
    supplierBankName: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const partnerListItemSchema = partnerSchema.pick({
    id: true,
    migrationReviewRequired: true,
    hasMigrationSource: true,
    partnerNumber: true,
    type: true,
    status: true,
    isActive: true,
    isCustomer: true,
    isProspect: true,
    isSupplier: true,
    displayName: true,
    email: true,
    phone: true,
    postalCode: true,
    city: true,
    countryCode: true,
    subdivisionCode: true,
    priceGroupId: true,
    priceGroupName: true,
    priceGroup: true,
    subdivisionName: true,
    industry: true,
    industryCode: true,
    salesTerritory: true,
    revenueClass: true,
    vatId: true,
    siren: true,
    siret: true,
    vatIdStatus: true,
    ownerName: true,
    tags: true,
    customFields: true,
    debtorAccountNumber: true,
    paymentTermsDays: true,
    creditLimit: true,
    riskStatus: true,
    isBlocked: true,
    blockReason: true,
    creditInsuranceStatus: true,
    creditInsuranceLimit: true,
    leadStatus: true,
    leadSource: true,
    leadScore: true,
    dealSizeEstimated: true,
    winProbability: true,
    expectedClosingDate: true,
    creditorAccountNumber: true,
    supplierRating: true,
    minimumOrderValue: true,
    deliveryTimeDays: true,
    createdAt: true,
    updatedAt: true,
});
// ---------------------------------------------------------------------------
// Partner (Schreibmodell)
// ---------------------------------------------------------------------------
export const partnerWriteSchema = z.object({
    /** Leer lassen: Die Nummer wird rollenabhängig aus dem passenden Nummernkreis vergeben. */
    partnerNumber: z.string().trim().min(1).max(30).nullish(),
    type: customerTypeSchema,
    status: customerStatusSchema.default('ACTIVE'),
    isCustomer: z.boolean().default(false),
    isProspect: z.boolean().default(false),
    isSupplier: z.boolean().default(false),
    companyName: optionalText(200),
    tradingName: optionalText(200),
    legalForm: legalFormSchema.nullish(),
    ...internationalLegalFormFields,
    department: optionalText(100),
    firstName: optionalText(100),
    lastName: optionalText(100),
    email: z.string().trim().email().max(200).nullish(),
    phone: optionalText(50),
    mobile: optionalText(50),
    website: z.string().trim().url().max(200).nullish(),
    street: optionalText(200),
    geocodingPublic: z.boolean().optional(),
    houseNumber: optionalText(20),
    addressLine2: optionalText(200),
    postalCode: optionalText(20),
    city: optionalText(100),
    region: optionalText(100),
    regionOrigin: addressRegionOriginSchema,
    ...geographicSubdivisionFields,
    countryCode: countryCodeSchema.default('DE'),
    vatId: vatIdInputSchema,
    taxNumber: optionalText(30),
    siren: sirenInputSchema,
    siret: siretInputSchema,
    gln: z
        .string()
        .trim()
        .regex(/^\d{13}$/, 'Die GLN besteht aus 13 Ziffern.')
        .nullish(),
    eoriNumber: optionalText(20),
    taxCategory: taxCategorySchema.default('DOMESTIC'),
    preferredLanguage: z.enum(DOCUMENT_LANGUAGES).default('tr'),
    currency: currencyCodeSchema.default('EUR'),
    industry: customerIndustrySchema.nullish(),
    industryCode: customerIndustryCodeInputSchema,
    salesTerritory: optionalText(120),
    revenueClass: optionalText(120),
    ownerUserId: idSchema.nullish(),
    tags: partnerTagsSchema.default([]),
    customFields: customFieldValuesSchema.default({}),
    // Kunde
    debtorAccountNumber: debtorAccountSchema.nullish(),
    creditLimit: moneySchema.nullish(),
    creditRating: z.number().int().min(0).max(1000).nullish(),
    priceGroupId: idSchema.nullish(),
    priceGroup: optionalText(50),
    defaultDiscountPct: percentSchema.default('0'),
    paymentTermId: idSchema.nullish(),
    paymentTermsDays: z.number().int().min(0).max(365).default(14),
    discountDays: z.number().int().min(0).max(365).default(0),
    discountPercent: percentSchema.default('0'),
    deliveryTerms: optionalText(160),
    deliveryBlock: z.boolean().default(false),
    dunningBlock: z.boolean().default(false),
    minRemainingShelfLifeDays: z.number().int().min(0).max(3650).nullish(),
    // Interessent
    leadStatus: leadStatusSchema.nullish(),
    leadSource: leadSourceSchema.nullish(),
    leadScore: z.number().int().min(LEAD_SCORE_MIN).max(LEAD_SCORE_MAX).nullish(),
    dealSizeEstimated: moneySchema.nullish(),
    winProbability: z.number().int().min(0).max(100).nullish(),
    expectedClosingDate: isoDateSchema.nullish(),
    lostReason: optionalText(200),
    // Lieferant
    creditorAccountNumber: creditorAccountSchema.nullish(),
    supplierRating: supplierRatingSchema.nullish(),
    minimumOrderValue: moneySchema.nullish(),
    deliveryTimeDays: z.number().int().min(0).max(365).nullish(),
    incoterms: incotermSchema.nullish(),
    supplierIban: ibanSchema.nullish(),
    supplierBic: bicSchema.nullish(),
    supplierBankName: optionalText(120),
});
/**
 * Firmen brauchen einen Firmennamen, Privatpersonen Vor- und Nachnamen. Die Prüfung
 * läuft zusätzlich serverseitig gegen den zusammengeführten Datensatz, weil ein
 * PATCH nur Teilfelder liefert.
 */
export function hasRequiredPartnerName(input) {
    if (input.type === 'COMPANY') {
        return typeof input.companyName === 'string' && input.companyName.trim().length > 0;
    }
    return (typeof input.firstName === 'string' &&
        input.firstName.trim().length > 0 &&
        typeof input.lastName === 'string' &&
        input.lastName.trim().length > 0);
}
export const partnerCreateSchema = partnerWriteSchema
    .extend({ creationMode: partnerCreationModeSchema.default('QUICK') })
    .superRefine((value, ctx) => {
    validateSubdivision(value, ctx);
    refineFrenchCompanyIdentifiers(value, ctx);
    if (value.legalFormCode === 'OTHER' && !value.legalFormName?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['legalFormName'],
            message: 'international.otherRequired',
        });
    }
    if (!hasRequiredPartnerName(value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: value.type === 'COMPANY' ? ['companyName'] : ['lastName'],
            message: value.type === 'COMPANY'
                ? 'Für Firmen ist der Firmenname erforderlich.'
                : 'Für Privatpersonen sind Vor- und Nachname erforderlich.',
        });
    }
    if (!hasAnyPartnerRole(value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['isCustomer'],
            message: 'Mindestens eine Rolle (Kunde, Interessent oder Lieferant) ist erforderlich.',
        });
    }
});
export const partnerUpdateSchema = partnerWriteSchema
    .extend({
    countryCode: countryCodeSchema.nullable(),
    taxCategory: taxCategorySchema.nullable(),
    preferredLanguage: z.enum(DOCUMENT_LANGUAGES).nullable(),
    defaultDiscountPct: percentSchema.nullable(),
    paymentTermsDays: z.number().int().min(0).max(365).nullable(),
    discountDays: z.number().int().min(0).max(365).nullable(),
    discountPercent: percentSchema.nullable(),
})
    .partial()
    .superRefine(refineFrenchCompanyIdentifiers);
export const partnerCreateDefaultsSchema = z.object({
    countryCode: countryCodeSchema,
    currency: currencyCodeSchema,
    preferredLanguage: z.enum(DOCUMENT_LANGUAGES),
    taxCategory: taxCategorySchema,
    paymentTermId: idSchema.nullable(),
    paymentTermsDays: z.number().int().min(0).max(365),
    discountDays: z.number().int().min(0).max(365),
    discountPercent: percentSchema,
});
// ---------------------------------------------------------------------------
// Risikomanagement und Warenkreditversicherung
// ---------------------------------------------------------------------------
const blockReasonSchema = z.string().trim().min(1).max(PARTNER_BLOCK_REASON_MAX_LENGTH);
/**
 * Sperrt einen Partner für Verkauf und Lieferung. Die Begründung ist Pflicht, weil
 * sie im Audit-Log und in jeder Warnmeldung der Oberfläche erscheint (z. B. das
 * Aktenzeichen des Insolvenzgerichts oder das Übergabedatum an das Inkassobüro).
 */
export const partnerLockSchema = z.object({
    riskStatus: z.enum(PARTNER_LOCK_RISK_STATUSES),
    blockReason: blockReasonSchema,
});
export const partnerUnlockSchema = z.object({
    unblockReason: blockReasonSchema,
    /** Einstufung nach der Freigabe; bewusst getrennt vom Sperrgrund dokumentiert. */
    riskStatus: z.enum(['NORMAL', 'WATCHLIST']).default('WATCHLIST'),
});
export const partnerInsuranceUpdateSchema = z
    .object({
    creditInsuranceStatus: creditInsuranceStatusSchema,
    creditInsuranceCompany: optionalText(120),
    creditInsurancePolicyNumber: optionalText(60),
    creditInsuranceLimit: moneySchema.nullish(),
    creditInsuranceDeductiblePercent: percentSchema.nullish(),
    creditInsuranceClaimDate: isoDateSchema.nullish(),
    creditInsuranceClaimAmount: moneySchema.nullish(),
    creditInsurancePayoutAmount: moneySchema.nullish(),
})
    .superRefine((value, ctx) => {
    const needsClaim = value.creditInsuranceStatus === 'CLAIM_REPORTED' ||
        value.creditInsuranceStatus === 'INDEMNIFIED';
    if (needsClaim && (value.creditInsuranceClaimDate ?? null) === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['creditInsuranceClaimDate'],
            message: 'Für eine Schadensmeldung ist das Meldedatum erforderlich.',
        });
    }
    if (needsClaim && (value.creditInsuranceClaimAmount ?? null) === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['creditInsuranceClaimAmount'],
            message: 'Für eine Schadensmeldung ist die Schadenssumme erforderlich.',
        });
    }
});
// ---------------------------------------------------------------------------
// Pipeline und Umwandlung
// ---------------------------------------------------------------------------
export const leadStageUpdateSchema = z
    .object({
    leadStatus: leadStatusSchema,
    winProbability: z.number().int().min(0).max(100).nullish(),
    expectedClosingDate: isoDateSchema.nullish(),
    lostReason: optionalText(200),
})
    .superRefine((value, ctx) => {
    if (value.leadStatus === 'LOST' &&
        (value.lostReason === undefined ||
            value.lostReason === null ||
            value.lostReason.trim().length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lostReason'],
            message: 'Für verlorene Interessenten ist ein Grund erforderlich.',
        });
    }
});
export const partnerConvertSchema = z.object({
    /** Leer lassen: Es wird die nächste freie DATEV-Debitorennummer vergeben. */
    debtorAccountNumber: debtorAccountSchema.nullish(),
    /** Leer lassen: Die bestehende Partnernummer bleibt erhalten. */
    partnerNumber: z.string().trim().min(1).max(30).nullish(),
    paymentTermId: idSchema.nullish(),
    paymentTermsDays: z.number().int().min(0).max(365).optional(),
    discountDays: z.number().int().min(0).max(365).optional(),
    discountPercent: percentSchema.optional(),
    creditLimit: moneySchema.nullish(),
    priceGroupId: idSchema.nullish(),
    priceGroup: optionalText(50),
    /** Bleibt der Partner zusätzlich als Interessent geführt (Cross-Selling-Pipeline)? */
    keepProspectRole: z.boolean().default(false),
});
export const leadPipelineColumnSchema = z.object({
    leadStatus: leadStatusSchema,
    count: z.number().int(),
    dealSizeTotal: z.string(),
    weightedDealSize: z.string(),
});
export const leadPipelineResponseSchema = z.object({
    columns: z.array(leadPipelineColumnSchema),
    currency: z.string(),
});
// ---------------------------------------------------------------------------
// Listenabfrage und Export
// ---------------------------------------------------------------------------
export const partnerListQuerySchema = paginationQuerySchema
    .omit({ sort: true })
    .extend({
    sort: z
        .string()
        .regex(/^[a-zA-Z]+:(asc|desc)$/)
        .optional(),
    type: partnerTypeSchema.default('all'),
    ...partnerMapFiltersShape,
    status: commaSeparated(customerStatusSchema),
    industry: commaSeparated(customerIndustrySchema),
    industryCode: customerIndustryCodeSchema.optional(),
    tags: commaSeparated(partnerTagSchema),
    leadStatus: commaSeparated(leadStatusSchema),
    leadSource: commaSeparated(leadSourceSchema),
    supplierRating: commaSeparated(supplierRatingSchema),
    riskStatus: commaSeparated(partnerRiskStatusSchema),
    countryCode: countryCodeSchema.optional(),
    priceGroupIds: commaSeparated(z.union([idSchema, z.literal('NONE')])),
    subdivisionCode: commaSeparated(z.string().trim().min(1).max(20)),
    ownerUserId: idSchema.optional(),
    vatIdStatus: commaSeparated(vatIdStatusSchema),
    customFieldFilters: customFieldFiltersQuerySchema,
})
    .refine((v) => [v.nearLat, v.nearLng, v.radiusKm].filter((x) => x !== undefined).length % 3 === 0, { message: 'partnerMap.invalidRadius' })
    .transform((value) => {
    const [field, order] = (value.sort ?? 'searchTerm:asc').split(':');
    const parsedField = partnerSortFieldSchema.safeParse(field);
    const parsedOrder = sortOrderSchema.safeParse(order);
    return {
        ...value,
        sortField: parsedField.success ? parsedField.data : 'searchTerm',
        sortOrder: parsedOrder.success ? parsedOrder.data : 'asc',
    };
});
export const partnerExportFormatSchema = z.enum(CUSTOMER_EXPORT_FORMATS);
export const partnerListResponseSchema = z.object({
    data: z.array(partnerListItemSchema),
    meta: paginationMetaSchema,
    facets: z.object({
        tags: z.array(z.object({ value: z.string(), count: z.number().int() })),
        roles: z.object({
            customer: z.number().int(),
            prospect: z.number().int(),
            supplier: z.number().int(),
        }),
    }),
});
// ---------------------------------------------------------------------------
// Lieferanten-Artikelverknüpfung
// ---------------------------------------------------------------------------
export const supplierProductSchema = z.object({
    id: idSchema,
    supplierPartnerId: idSchema,
    supplierName: z.string(),
    productId: idSchema,
    articleNumber: z.string(),
    productName: z.string(),
    baseUnit: z.string(),
    supplierSku: z.string().nullable(),
    purchasePrice: z.string().nullable(),
    priceTiers: z.array(supplierPriceTierSchema).optional(),
    currency: z.string(),
    packagingUnit: z.string().nullable(),
    packagingFactor: z.string().nullable(),
    leadTimeDays: z.number().int(),
    minOrderQuantity: z.string(),
    isPreferredSupplier: z.boolean(),
    notes: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const supplierProductWriteSchema = z.object({
    productId: idSchema,
    supplierSku: optionalText(60),
    purchasePrice: moneySchema.nullable(),
    currency: currencyCodeSchema.default('EUR'),
    packagingUnit: optionalText(30),
    packagingFactor: z
        .string()
        .trim()
        .regex(/^\d{1,12}(\.\d{1,6})?$/, 'Erwartet wird eine positive Dezimalzahl.')
        .nullish(),
    leadTimeDays: z.number().int().min(0).max(365).default(0),
    minOrderQuantity: z
        .string()
        .trim()
        .regex(/^\d{1,12}(\.\d{1,6})?$/, 'Erwartet wird eine nicht negative Dezimalzahl.')
        .default('0'),
    isPreferredSupplier: z.boolean().default(false),
    notes: optionalText(500),
});
export const supplierProductUpdateSchema = supplierProductWriteSchema
    .omit({ productId: true })
    .partial();
export const supplierProductListResponseSchema = z.object({
    data: z.array(supplierProductSchema),
});
// ---------------------------------------------------------------------------
// Bestellhistorie eines Lieferanten
// ---------------------------------------------------------------------------
export const supplierOrderSchema = z.object({
    id: idSchema,
    documentNumber: z.string(),
    type: z.enum(['PURCHASE_ORDER']),
    status: z.string(),
    documentDate: isoDateSchema,
    deliveryDate: isoDateSchema.nullable(),
    totalNet: z.string(),
    totalGross: z.string(),
    currency: z.string(),
    itemCount: z.number().int(),
});
export const supplierOrderListResponseSchema = z.object({
    data: z.array(supplierOrderSchema),
});
// ---------------------------------------------------------------------------
// Kundenkennzahlen (Detailansicht)
// ---------------------------------------------------------------------------
export const partnerSalesSummarySchema = z.object({
    currency: z.string(),
    revenueYtd: z.string(),
    revenuePreviousYear: z.string(),
    revenueTotal: z.string(),
    revenueChangeAmount: z.string(),
    revenueChangePercent: z.string().nullable(),
    orderIntake: z.string(),
    quoteCount: z.number().int(),
    orderCount: z.number().int(),
    deliveryNoteCount: z.number().int(),
    invoiceCount: z.number().int(),
    averageOrderValue: z.string(),
    openInvoiceAmount: z.string(),
    openInvoiceCount: z.number().int(),
    overdueInvoiceAmount: z.string(),
    overdueInvoiceCount: z.number().int(),
    paidAmount: z.string(),
    creditsAndCancellations: z.string(),
    openQuoteCount: z.number().int(),
    openOrderCount: z.number().int(),
    /** Mittleres vereinbartes Zahlungsziel über alle Rechnungen dieses Partners. */
    averagePaymentTermsDays: z.number().int().nullable(),
    lastOrderDate: isoDateSchema.nullable(),
    lastInvoiceDate: isoDateSchema.nullable(),
});
export const partnerSalesSummaryQuerySchema = z.object({
    dateFrom: isoDateSchema.optional(),
    dateTo: isoDateSchema.optional(),
});
//# sourceMappingURL=partner.js.map